/**
 * This is a standalone component that renders a clickable behavior for the
 * Khanmigo activities link in the header. It wraps an icon with the text "Khanmigo".
 */

import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";
import {useContext} from "react";

import Clickable from "@khanacademy/wonder-blocks-clickable";
import type {StyleType} from "@khanacademy/wonder-blocks-core";
import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import {spacing} from "@khanacademy/wonder-blocks-tokens";
import {LabelMedium} from "@khanacademy/wonder-blocks-typography";
import {a11yStyles} from "@repo/shared-styles";

import {GuideIcon32} from "#guide/ui/icons/guide-icon-32.tsx";

import {BackgroundContext} from "./background-context.ts";

import caretDownIcon from "@phosphor-icons/core/fill/caret-down-fill.svg";
import caretUpIcon from "@phosphor-icons/core/fill/caret-up-fill.svg";

const KhanmigoActivitiesClickable = ({
    navItemStyles,
    isOpen,
    ...props
}: Omit<React.ComponentProps<typeof Clickable>, "children"> & {
    navItemStyles: StyleType;
    isOpen?: boolean; // isOpen prop will only be passed in when the clickable is a dropdown, NOT a link
}): React.ReactElement | null => {
    const isBackgroundWhite = useContext(BackgroundContext);
    return (
        <Clickable
            {...props}
            target={undefined}
            style={[navItemStyles, styles.group]}
            role="button"
            testId="khanmigo-clickable"
        >
            {() => (
                <>
                    <GuideIcon32
                        id="khanmigo-icon"
                        style={
                            isOpen !== undefined
                                ? styles.khanmigoIconWithTriangle
                                : styles.khanmigoIconNoTriangle
                        }
                    />
                    <LabelMedium style={styles.hideTextBreakpoint}>
                        {t`Khanmigo`}
                    </LabelMedium>

                    {/* The caret icon will only appear when the clickable opens a dropdown */}
                    {isOpen !== undefined && (
                        <PhosphorIcon
                            style={[
                                styles.triangle,
                                !isBackgroundWhite
                                    ? styles.transparentTriangle
                                    : {},
                            ]}
                            icon={isOpen ? caretUpIcon : caretDownIcon}
                            size="small"
                        />
                    )}
                </>
            )}
        </Clickable>
    );
};

const styles = StyleSheet.create({
    group: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "right",
        height: "100%",
        boxShadow: "none",
        outline: "none",
    },
    khanmigoIconNoTriangle: {
        width: "32px",
        "@media screen and (max-width: 767px) /* mediaQueries.smOrSmaller */": {
            marginRight: spacing.xSmall_8,
        },
    },
    khanmigoIconWithTriangle: {
        width: "32px",
    },
    hideTextBreakpoint: {
        marginInlineStart: 5,
        fontWeight: "bold",
        "@media screen and (max-width: 1199px) /* mediaQueries.lgOrSmaller */":
            a11yStyles.srOnly,
    },
    triangle: {
        marginLeft: spacing.xxSmall_6,
        position: "relative",
        "@media screen and (max-width: 1199px) /* mediaQueries.lgOrSmaller */":
            {
                marginLeft: 0,
            },
    },
    transparentTriangle: {
        color: "rgba(255, 255, 255, 0.48)",
    },
});

export default KhanmigoActivitiesClickable;
